'use client'

import type { UseQueryResult } from '@tanstack/react-query'
import { graphql } from '~/gql'
import { GetClientSetupQuery, KeyValue } from '~/gql/graphql'
import { useGQLQuery } from '~/hooks/useGraphQL'

export const clientSetupStaleTime = 10 * 60 * 1000

const clientSetupQuery = graphql(`
  query GetClientSetup($workspaceId: ID!) {
    getClientSetup(workspaceId: $workspaceId) {
      orgName
      menus
      tabs
      tables
      reportSectionsTweaks
      setups {
        key
        value
      }
    }
  }
`)

export type ClientSetupMenuItem = {
  key: string
  url: string
  icon: string
  caption: string
  'sub-menus'?: ClientSetupMenuItem[]
}

export type ClientSetupMenus = {
  evaluation?: ClientSetupMenuItem[]
  evaluationCommandMenu?: ClientSetupMenuItem[]
}

export type ClientSetupTabItem = {
  key: string
  icon: string
  type: string
  label: string
}

export type ClientSetupTabs = {
  evaluation?: ClientSetupTabItem[]
}

export type ClientSetupMatrixTitles = {
  meets_all?: string
  meets_critical_not_important?: string
  meets_important_not_critical?: string
  meets_none?: string
}

export type ClientSetupTables = {
  tableXlsxExportMapping?: Record<string, string>
  tableAddElementMapping?: Record<string, string>
}

export type ClientSetupProductCards = {
  productRankCardCaptions?: Record<string, string>
}

export type ClientSetupReportSectionsTweaks = {
  matrixTitles?: ClientSetupMatrixTitles
  productCards?: ClientSetupProductCards
}

export type ClienSetupQueryResult = {
  orgName?: string
  menus?: ClientSetupMenus
  tabs?: ClientSetupTabs
  tables?: ClientSetupTables
  reportSectionsTweaks?: ClientSetupReportSectionsTweaks
  setups: KeyValue[]
}

/**
 * The GraphQL API now returns menus and tabs as JSON objects directly,
 * so no parsing is needed.
 */
export type UseClientSetupQueryResult = UseQueryResult<GetClientSetupQuery>

export const useClientSetupQuery = (
  workspaceId: string,
  enabled = true
): UseClientSetupQueryResult => {
  return useGQLQuery(
    clientSetupQuery,
    { workspaceId },
    // Set stale time as 10 minutes so the evaluations aren't fetched too often
    { enabled, staleTime: clientSetupStaleTime }
  )
}
